import type { OrganizationBillingCategoryCode } from "@api/shared-runtime/enums";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CATEGORY_FILTER_OPTIONS,
  DEFAULT_EVENT_FILTERS,
  hasActiveEventFilters,
  SORT_DIRECTION_OPTIONS,
  SOURCE_FILTER_OPTIONS,
  VOID_STATUS_FILTER_OPTIONS,
  type UsageBillingEventFilters,
  type UsageEventSortDirection,
  type UsageEventSourceFilter,
  type UsageEventVoidStatusFilter,
} from "./usage-billing-event-filters";

// Radix Select items can't carry an empty-string value, so the "all
// categories"/"all sources" options use this sentinel and get translated
// back to undefined before reaching the filters state.
const ALL_OPTIONS_VALUE = "all";

const SELECT_TRIGGER_CLASSNAME = "h-8 w-full py-1 text-xs sm:w-40";

type UsageBillingEventFilterBarProps = {
  filters: UsageBillingEventFilters;
  onChange: (filters: UsageBillingEventFilters) => void;
};

export function UsageBillingEventFilterBar({
  filters,
  onChange,
}: UsageBillingEventFilterBarProps) {
  const showClearFilters = hasActiveEventFilters(filters);

  return (
    <div className="flex flex-col gap-2 border-b bg-gray-50 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center">
      <Select
        value={filters.categoryCode ?? ALL_OPTIONS_VALUE}
        onValueChange={(value) =>
          onChange({
            ...filters,
            categoryCode:
              value === ALL_OPTIONS_VALUE
                ? undefined
                : (value as OrganizationBillingCategoryCode),
          })
        }
      >
        <SelectTrigger className={SELECT_TRIGGER_CLASSNAME}>
          <SelectValue placeholder="Category" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_OPTIONS_VALUE}>All categories</SelectItem>
          {CATEGORY_FILTER_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.source ?? ALL_OPTIONS_VALUE}
        onValueChange={(value) =>
          onChange({
            ...filters,
            source:
              value === ALL_OPTIONS_VALUE
                ? undefined
                : (value as UsageEventSourceFilter),
          })
        }
      >
        <SelectTrigger className={SELECT_TRIGGER_CLASSNAME}>
          <SelectValue placeholder="Source" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_OPTIONS_VALUE}>All sources</SelectItem>
          {SOURCE_FILTER_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.voidStatus}
        onValueChange={(value) =>
          onChange({
            ...filters,
            voidStatus: value as UsageEventVoidStatusFilter,
          })
        }
      >
        <SelectTrigger className={SELECT_TRIGGER_CLASSNAME}>
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          {VOID_STATUS_FILTER_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={filters.sortDirection}
        onValueChange={(value) =>
          onChange({
            ...filters,
            sortDirection: value as UsageEventSortDirection,
          })
        }
      >
        <SelectTrigger className={SELECT_TRIGGER_CLASSNAME}>
          <SelectValue placeholder="Sort" />
        </SelectTrigger>
        <SelectContent>
          {SORT_DIRECTION_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {showClearFilters && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 self-start px-2 text-xs text-gray-600 hover:text-gray-900 sm:ml-auto"
          onClick={() => onChange(DEFAULT_EVENT_FILTERS)}
        >
          Clear filters
        </Button>
      )}
    </div>
  );
}
